import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { createGroup, updateGroup, type GroupDetail } from '../../../../types'
import { formSubmit } from '../../api/actionApiCall'
import { CheckboxField, CurrencyPicker, SelectField, SubmitButton, TextField, useFormState } from '../../components/form'
import { useCurrencies } from '../../hooks/useCurrencies'

export type GroupFormModalProps = {
    show: boolean
    /** Bez skupiny jde o založení nové. */
    group?: GroupDetail
    onClose: () => void
    onSaved: (groupId: string) => void
}

export function GroupFormModal({ show, group, onClose, onSaved }: GroupFormModalProps) {
    const isEdit = !!group
    const { data: currencies } = useCurrencies()

    const form = useFormState({
        initialValues: {
            name: group?.name ?? '',
            baseCurrencyCode: group?.baseCurrencyCode ?? 'CZK',
            currencyCodes: group?.currencyCodes ?? ['CZK'],
            simplifyDebts: group?.simplifyDebts ?? false,
        },
        onSubmit: async (values) => {
            // Hlavní měna musí být vždy mezi povolenými, i když ji uživatel neoznačí.
            const currencyCodes = [...new Set([values.baseCurrencyCode, ...values.currencyCodes])]

            const { data, fieldErrors } = isEdit
                ? await formSubmit(
                      () =>
                          updateGroup({
                              path: { groupId: group.id },
                              body: {
                                  name: values.name,
                                  baseCurrencyCode: values.baseCurrencyCode,
                                  currencyCodes,
                                  simplifyDebts: values.simplifyDebts,
                              },
                          }),
                      { successMessage: 'Skupina byla upravena.' },
                  )
                : await formSubmit(
                      () =>
                          createGroup({
                              body: {
                                  name: values.name,
                                  baseCurrencyCode: values.baseCurrencyCode,
                                  currencyCodes,
                                  simplifyDebts: values.simplifyDebts,
                              },
                          }),
                      { successMessage: 'Skupina byla založena.' },
                  )

            if (!data) {
                return { fieldErrors }
            }

            onSaved(data.id)
            onClose()
        },
    })

    return (
        <Modal show={show} onHide={onClose} centered scrollable>
            <form onSubmit={form.handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5">{isEdit ? 'Úprava skupiny' : 'Nová skupina'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TextField
                        label="Název skupiny"
                        value={form.values.name}
                        onChange={(value) => form.setValue('name', value)}
                        error={form.fieldErrors.name}
                        placeholder="Např. Dovolená v Chorvatsku"
                        required
                        autoFocus
                    />
                    <SelectField
                        label="Hlavní měna"
                        value={form.values.baseCurrencyCode}
                        onChange={(value) => form.setValue('baseCurrencyCode', value)}
                        error={form.fieldErrors.baseCurrencyCode}
                        helpText="V této měně se počítají bilance a návrhy úhrad."
                        options={(currencies ?? []).map((currency) => ({ value: currency.code, label: `${currency.code} — ${currency.name}` }))}
                    />
                    <CurrencyPicker
                        label="Povolené měny transakcí"
                        currencies={currencies ?? []}
                        selected={form.values.currencyCodes}
                        lockedCode={form.values.baseCurrencyCode}
                        onChange={(codes) => form.setValue('currencyCodes', codes)}
                        error={form.fieldErrors.currencyCodes}
                        helpText="Vyberte měny, ve kterých budete ve skupině platit."
                    />
                    <CheckboxField
                        label="Minimalizovat převody"
                        checked={form.values.simplifyDebts}
                        onChange={(checked) => form.setValue('simplifyDebts', checked)}
                        helpText="Výchozí nastavení přepínače u přehledu dluhů; jde kdykoli přepnout."
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                        Zrušit
                    </Button>
                    <SubmitButton submitting={form.submitting}>{isEdit ? 'Uložit' : 'Založit'}</SubmitButton>
                </Modal.Footer>
            </form>
        </Modal>
    )
}
